import React, { VFC } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { RegionCityPageCard } from '../pages/Top';
import { TEXT_SECONDARY } from '../../core/styles/variables';

type Props = RegionCityPageCard & {
  className?: string;
};

const Card: VFC<Props> = ({
  id,
  prefectureName,
  cityName,
  cityNameHirakana,
  description,
  coverImageUrl,
  className,
}) => {
  const classes = useStyles();
  return (
    <a href={`/cities/${id}`} className={clsx(classes.root, className)}>
      <div className={classes.imageWrap}>
        {coverImageUrl ? (
          <img
            src={coverImageUrl}
            alt={cityName}
            className={classes.image}
            loading="lazy"
          />
        ) : (
          <div className={classes.noImage} />
        )}
      </div>
      <div className={classes.body}>
        <div className={classes.titleBlock}>
          <div className={classes.badgeWrap}>
            <span className={classes.badge}>{prefectureName}</span>
          </div>
          <div className={classes.nameBlock}>
            <span className={classes.furigana}>{cityNameHirakana}</span>
            <span className={classes.cityName}>{cityName}</span>
          </div>
        </div>
        <div className={classes.descWrap}>
          <span className={classes.description}>{description}</span>
        </div>
      </div>
    </a>
  );
};

export default Card;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    borderRadius: 4,
    border: '1px solid #E5E5E5',
    backgroundColor: '#fff',
    boxShadow: theme.shadows[4],
    textDecoration: 'none',
    color: '#333',
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 0.97,
      textDecoration: 'none',
    },
    '&:hover $cityName': {
      textDecoration: 'underline',
      color: theme.palette.text.primary,
      textDecorationColor: theme.palette.text.primary,
    },
  },
  imageWrap: {
    position: 'relative',
    width: '100%',
    flexShrink: 0,
    overflow: 'hidden',
    height: 163,
    [theme.breakpoints.up('sm')]: {
      height: 282,
    },
  },
  image: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  noImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#eee',
  },
  body: {
    display: 'flex',
    minHeight: 0,
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '#fff',
  },
  titleBlock: {
    display: 'flex',
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  },
  badgeWrap: {
    display: 'flex',
    flexShrink: 0,
    paddingBottom: 2,
  },
  badge: {
    display: 'inline-flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontWeight: 600,
    padding: '2px 4px',
    fontSize: theme.typography.pxToRem(12),
    [theme.breakpoints.up('lg')]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  nameBlock: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    alignSelf: 'flex-end',
  },
  furigana: {
    position: 'absolute',
    left: 0,
    top: -12,
    minWidth: 0,
    textAlign: 'left',
    fontSize: theme.typography.pxToRem(10),
    color: TEXT_SECONDARY,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  cityName: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.pxToRem(16),
    },
  },
  descWrap: {
    display: 'flex',
    flex: 1,
    alignContent: 'flex-end',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: theme.typography.pxToRem(12),
    color: TEXT_SECONDARY,
    [theme.breakpoints.up('sm')]: {
      fontSize: theme.typography.pxToRem(14),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  },
  description: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
  },
}));
